import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, categoryLabel } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const TIME_SLOTS = ["Morning (10 AM – 1 PM)", "Afternoon (1 PM – 4 PM)", "Evening (4 PM – 8 PM)"];

export default function Consultation() {
  const [searchParams] = useSearchParams();
  const prefill = searchParams.get("product") || "";
  const [products, setProducts] = useState([]);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    preferred_date: "",
    preferred_time: TIME_SLOTS[0],
    product_interest: prefill,
    message: "",
  });

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (prefill) setForm((f) => ({ ...f, product_interest: prefill }));
  }, [prefill]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/consultations", form);
      setDone(true);
      toast.success("Your consultation request has been received. We will reach out within 24 hours.");
    } catch {
      toast.error("Could not submit your request. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 pb-24 pt-40 lg:px-8 lg:pb-32" data-testid="consultation-page">
      <div className="grid gap-20 lg:grid-cols-2">
        <Reveal>
          <p className="overline">Private Consultation</p>
          <h1 className="mt-4 font-serif text-4xl font-light leading-tight text-[#262019] sm:text-5xl lg:text-6xl">
            Let us find your <span className="italic text-[#A58343]">forever piece</span>
          </h1>
          <p className="mt-8 max-w-md text-base leading-relaxed text-[#7C7163]">
            A relaxed, one-on-one video or phone call with our jewellery specialist.
            We will understand your occasion, style and budget, show you pieces live
            on camera, and answer every question — with zero pressure to buy.
          </p>
          <div className="mt-12 space-y-8 border-t border-[#A58343]/20 pt-12">
            {[
              ["01", "Tell us what you are dreaming of"],
              ["02", "Meet your specialist on a private call"],
              ["03", "See pieces live, ask anything, decide in your own time"],
            ].map(([n, t]) => (
              <div key={n} className="flex items-baseline gap-6">
                <span className="font-serif text-2xl italic text-[#A58343]">{n}</span>
                <p className="text-sm tracking-wide text-[#262019]/80">{t}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          {done ? (
            <div className="grain flex h-full flex-col items-center justify-center border border-[#A58343]/25 bg-[#EFE8DC] p-16 text-center" data-testid="consultation-success">
              <p className="overline">Request Received</p>
              <h2 className="mt-6 font-serif text-4xl font-light text-[#262019]">
                We look forward to <span className="italic text-[#A58343]">meeting you</span>
              </h2>
              <p className="mt-6 max-w-sm text-sm leading-relaxed text-[#262019]/70">
                Our specialist will call you within 24 hours to confirm your slot.
                Meanwhile, feel free to keep exploring the collection.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="grain border border-[#A58343]/25 bg-[#FFFFFF] p-6 sm:p-10 lg:p-14" data-testid="consultation-form">
              <div className="space-y-8">
                <div>
                  <label htmlFor="c-name" className="overline !text-[10px]">Full Name</label>
                  <input id="c-name" required value={form.name} onChange={set("name")} placeholder="Your name" className="field mt-2" data-testid="consultation-name-input" />
                </div>
                <div className="grid gap-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="c-phone" className="overline !text-[10px]">Phone</label>
                    <input id="c-phone" required type="tel" value={form.phone} onChange={set("phone")} placeholder="+91 …" className="field mt-2" data-testid="consultation-phone-input" />
                  </div>
                  <div>
                    <label htmlFor="c-email" className="overline !text-[10px]">Email</label>
                    <input id="c-email" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className="field mt-2" data-testid="consultation-email-input" />
                  </div>
                </div>
                <div className="grid gap-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="c-date" className="overline !text-[10px]">Preferred Date</label>
                    <input id="c-date" required type="date" value={form.preferred_date} onChange={set("preferred_date")} className="field mt-2 [color-scheme:light]" data-testid="consultation-date-input" />
                  </div>
                  <div>
                    <label htmlFor="c-time" className="overline !text-[10px]">Preferred Time</label>
                    <select id="c-time" value={form.preferred_time} onChange={set("preferred_time")} className="field mt-2 bg-transparent" data-testid="consultation-time-select">
                      {TIME_SLOTS.map((t) => (
                        <option key={t} value={t} className="bg-[#FFFFFF]">{t}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="c-product" className="overline !text-[10px]">Piece You Are Interested In (optional)</label>
                  <select id="c-product" value={form.product_interest} onChange={set("product_interest")} className="field mt-2 bg-transparent" data-testid="consultation-product-select">
                    <option value="" className="bg-[#FFFFFF]">Not sure yet — guide me</option>
                    {products.map((p) => (
                      <option key={p.slug} value={p.slug} className="bg-[#FFFFFF]">
                        {p.name} · {categoryLabel(p.category)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="c-message" className="overline !text-[10px]">Anything We Should Know? (optional)</label>
                  <textarea id="c-message" rows={3} value={form.message} onChange={set("message")} placeholder="Occasion, budget, questions…" className="field mt-2 resize-none" data-testid="consultation-message-input" />
                </div>
                <button type="submit" disabled={sending} className="btn-gold-solid w-full disabled:opacity-50" data-testid="consultation-submit-button">
                  {sending ? "Reserving your slot…" : "Reserve My Consultation"}
                </button>
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </div>
  );
}
